import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { axiosInstance } from "../../../provider/authProvider";
import { BE_BASE_URL, FE_BASE_URL } from "../../../utils/config";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { Color } from "../../../constants/color";
import { Bounce, ToastContainer, toast } from "react-toastify";
import SubmitButton from "../../../components/form/submit";
import Textbox from "../../../components/form/input";
import { goToPage } from "../../../utils/utils";

const FormWrapper = styled.form<{ $hasError: boolean }>`
  order: 2;
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
  position: relative;
  min-width: 25rem;
  min-height: 25rem;
  height: ${(props) => (props.$hasError ? "530px" : "unset")};
  max-height: 500px;
  margin: 3.2rem auto auto;
  border-radius: 2rem;
  background-color: ${Color.WHITE};
  box-shadow: 0 0 8px 4px #e2e2e2;
  button {
    margin: auto;
  }
`;

const Header = styled.h1`
  background-color: ${Color.LIGHT_BLUE};
  border-radius: 2rem 2rem 0 0;
  color: ${Color.WHITE};
  text-align: center;
  line-height: 80px;
  font-size: 20px;
  font-family: "Avenir Next Bold", sans-serif;
  margin: 2px 2px 0 2px;
`;

const ErrorMessage = styled.div<{ $visible: boolean }>`
  display: ${(props) => (props.$visible ? "block" : "none")};
  text-align: center;
  max-width: 20rem;
  height: 2rem;
  margin-top: 0.6rem;
  margin-left: 2.5rem;
  background-color: #ffc0cb; /* rosa (PINK) */
  border-radius: 0.6rem;
  border: 2px solid #ff0000; /* rosso (RED) */
  div {
    margin-top: 0.5rem;
  }
  font-family: "Avenir Next next", sans-serif;
  color: #ff0000; /* rosso (RED) */
`;

const LinkWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 1rem;
`;

const ChangePasswordForm: React.FC = () => {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState("");
  const [matchPassword, setMatchPassword] = useState("");
  const { token } = useParams<{ token: string }>();
  const [error, setError] = useState("");

  if (!token) {
    return (
      <div>
        <h1>{t("Errore")}</h1>
        <p>{t("Token non valido o mancante per il cambio password")}</p>
      </div>
    );
  }

  // Verifica se le password corrispondono e mostra un errore in caso contrario
  useEffect(() => {
    if (newPassword && matchPassword && newPassword !== matchPassword) {
      setError(t("password-matches"));
    } else {
      setError("");
    }
  }, [newPassword, matchPassword, t]);

  const savePass = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (newPassword !== matchPassword) {
      setError(t("password-matches"));
      return;
    }

    const formData = { newPassword, token };

    try {
      const response = await axiosInstance.post(
        BE_BASE_URL + "/user/savePassword",
        formData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200) {
        const message = response.data.message;
        toast.success(message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Bounce,
        });
        setTimeout(() => {
          goToPage(`login?message=${message}`);
        }, 5000); // Il redirect avviene dopo 5 secondi
      }
      // Dopo l'aggiornamento, reindirizza alla pagina di login con un messaggio
    } catch (err: any) {
      if (err.response && err.response.data) {
        const errorData = err.response.data as any;
        toast.error(errorData.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Bounce,
        });
        if (errorData.error && errorData.error.indexOf("InternalError") > -1) {
          window.location.href =
            FE_BASE_URL + "/login?message=" + errorData.message;
        }
      } else {
        setError(t("password-matches"));
      }
    }
  };

  return (
    <>
      <ToastContainer />
      <FormWrapper
        id="change-password"
        onSubmit={savePass}
        $hasError={error !== ""}
      >
        <Header>{t("new-password-header")}</Header>

        <Textbox
          alt="new-password"
          label={t("reset-password-new")}
          form="change-password"
          name="newPassword"
          type="password"
          autocomplete="newPassword"
          placeholder={t("reset-password-new")}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <Textbox
          alt="match-password"
          label={t("reset-password-confirm")}
          form="change-password"
          name="matchPassword"
          type="password"
          autocomplete="newPassword"
          placeholder={t("reset-password-confirm")}
          onChange={(e) => setMatchPassword(e.target.value)}
        />
        <input type="hidden" name="token" value={token} />
        <ErrorMessage $visible={error !== ""}>
          <div dangerouslySetInnerHTML={{ __html: error }} />
        </ErrorMessage>

        <SubmitButton
          label={t("reset-password-submit")}
          form="change-password"
          name="submit change password"
          style={{
            padding: "0.5rem 1.5rem",
          }}
        />

        <LinkWrapper style={{ justifyContent: "center" }}>
          <Link
            style={{
              color: Color.GREEN,
              fontFamily: "Avenir Next, sans-serif",
              fontWeight: "bold",
            }}
            to="/login"
          >
            {t("reset-password-login-link")}
          </Link>
        </LinkWrapper>
      </FormWrapper>
    </>
  );
};

export default ChangePasswordForm;
